import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { useToast } from "@/hooks/use-toast";
import { PageHeader } from "@/components/layout/page-header";
import { useAuth } from "@/lib/auth-context";
import { useAtualizarPraca, useCriarPraca, usePracasAdmin } from "@/hooks/use-cotacoes";

/**
 * As praças — o cadastro que sustenta as Cotações. Três campos, tela do RIZOMA.
 *
 * ⚠️ Sem excluir. O `slug` é a chave de `mercado_cotacoes.praca` e de
 * `organizations.praca`: apagar deixaria histórico órfão e orgs apontando para o
 * vazio. Praça que acabou vira **inativa** — some do seletor do produtor e o
 * histórico continua legível.
 *
 * ⚠️ O slug é derivado do nome no servidor e **não muda depois de criado**: por
 * isso ele é mostrado, e não editável. Renomear a praça é livre; renomear a chave
 * de mil cotações não é.
 */

export default function AdminPracasPage() {
  const { user } = useAuth();
  const { toast } = useToast();
  const ehPlataforma = !!user?.plataformaAdmin;

  const [nome, setNome] = useState("");
  const [uf, setUf] = useState("BA");

  const { data: pracas = [], isLoading } = usePracasAdmin(ehPlataforma);
  const criar = useCriarPraca();
  const atualizar = useAtualizarPraca();

  if (!ehPlataforma) {
    return (
      <div className="p-8 text-center text-muted-foreground">
        Acesso restrito ao administrador da plataforma.
      </div>
    );
  }

  function adicionar() {
    if (nome.trim().length < 2) {
      toast({ title: "Informe o nome da praça", variant: "destructive" });
      return;
    }
    criar.mutate(
      { nome: nome.trim(), uf: uf.trim().toUpperCase() },
      {
        onSuccess: p => { setNome(""); toast({ title: `Praça ${p.nome} criada.`, description: `Identificador: ${p.slug}` }); },
        onError: (e: Error) => toast({ title: "Não deu para criar", description: e.message, variant: "destructive" }),
      },
    );
  }

  return (
    <div className="p-4 md:p-6 space-y-4">
      <PageHeader
        titulo="Praças"
        descricao="Onde o preço se forma. O identificador é gerado do nome e não muda depois."
        empilharQuandoEstreito
      />

      <section className="rounded-lg border bg-card shadow-xs" data-testid="form-nova-praca">
        <header className="border-b px-3 py-2">
          <p className="text-sm font-semibold">Nova praça</p>
        </header>
        <div className="p-3 flex flex-wrap items-end gap-2">
          <div className="flex-1 min-w-[200px]">
            <Label className="text-xs uppercase text-muted-foreground">Nome</Label>
            <Input value={nome} onChange={e => setNome(e.target.value)} placeholder="Bom Jesus da Lapa" data-testid="input-nome-praca" />
          </div>
          <div className="w-24">
            <Label className="text-xs uppercase text-muted-foreground">UF</Label>
            <Input value={uf} onChange={e => setUf(e.target.value.toUpperCase().substring(0, 2))} maxLength={2} data-testid="input-uf-praca" />
          </div>
          <Button onClick={adicionar} disabled={criar.isPending} data-testid="button-criar-praca">
            {criar.isPending ? "Criando…" : "Adicionar"}
          </Button>
        </div>
      </section>

      <section className="rounded-lg border bg-card shadow-xs" data-testid="lista-pracas">
        <header className="border-b px-3 py-2">
          <p className="text-sm font-semibold">Praças cadastradas</p>
        </header>
        {isLoading ? (
          <div className="m-3 h-24 animate-pulse rounded-md bg-muted" />
        ) : pracas.length === 0 ? (
          <p className="py-8 text-center text-sm text-muted-foreground">Nenhuma praça cadastrada.</p>
        ) : (
          <ul className="divide-y">
            {pracas.map(p => (
              <li key={p.slug} className="flex flex-wrap items-center gap-3 px-3 py-2.5" data-testid={`praca-${p.slug}`}>
                <div className="min-w-0 flex-1">
                  <Input
                    className="h-9 font-medium"
                    defaultValue={p.nome}
                    onBlur={e => {
                      const novo = e.target.value.trim();
                      if (novo && novo !== p.nome) atualizar.mutate({ slug: p.slug, nome: novo });
                    }}
                    data-testid={`input-nome-${p.slug}`}
                  />
                  <p className="mt-0.5 text-[11px] text-muted-foreground font-mono">{p.slug}</p>
                </div>
                <Input
                  className="h-9 w-16 text-center uppercase"
                  defaultValue={p.uf}
                  maxLength={2}
                  onBlur={e => {
                    const novo = e.target.value.trim().toUpperCase();
                    if (novo.length === 2 && novo !== p.uf) atualizar.mutate({ slug: p.slug, uf: novo });
                  }}
                  data-testid={`input-uf-${p.slug}`}
                />
                <label className="flex items-center gap-2 text-xs text-muted-foreground">
                  <Switch
                    checked={p.ativa}
                    onCheckedChange={v => atualizar.mutate({ slug: p.slug, ativa: v })}
                    data-testid={`switch-ativa-${p.slug}`}
                  />
                  {p.ativa ? "ativa" : "inativa"}
                </label>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
